/* eslint-disable react/no-array-index-key */
import {
  Box,
  Collapsible,
  Flex,
  Grid,
  HStack,
  Link,
  List,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react';
import { BookIcon, ChevronDownIcon, DocumentIcon, FileIcon } from 'assets/icons/v2';
import { NovaIcon } from 'components/ui/novaIcon';
import { useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { ProtectedRoutes } from 'types/Routes';
import { BloomLevel, SessionPlan, SessionPlanActivity, SessionPlanPhase } from 'types/SessionPlan';

type Props = {
  plan: SessionPlan;
};

type PhaseColor = {
  solid: string;
  soft: string;
};

const PHASE_COLORS: PhaseColor[] = [
  { solid: 'yellow.500', soft: 'yellow.200' },
  { solid: 'green.500', soft: 'green.200' },
  { solid: 'purple.400', soft: 'purple.200' },
  { solid: 'red.400', soft: 'red.200' },
  { solid: 'blue.500', soft: 'blue.200' },
  { solid: 'skyBlue.500', soft: 'skyBlue.200' },
];

const bloomLevelLabel: Record<BloomLevel, string> = {
  remembering: 'erinnern',
  understanding: 'verstehen',
  applying: 'anwenden',
  analyzing: 'analysieren',
  evaluating: 'bewerten',
  creating: 'erschaffen',
};

const bloomBadgeStyles: Record<BloomLevel, { bg: string; color: string }> = {
  remembering: { bg: 'grey.200', color: 'text.secondary' },
  understanding: { bg: 'blue.300', color: 'blue.700' },
  applying: { bg: 'green.300', color: 'green.700' },
  analyzing: { bg: 'red.300', color: 'red.700' },
  evaluating: { bg: 'yellow.300', color: 'yellow.700' },
  creating: { bg: 'purple.300', color: 'purple.700' },
};

const SectionTitle = ({ children }: { children: ReactNode }): JSX.Element => (
  <Text textStyle="body.m.medium" color="text.primary" mb="m">
    {children}
  </Text>
);

const MetricCard = ({ label, value }: { label: string; value: string }): JSX.Element => (
  <Box
    bg="background.primary"
    border="1px solid"
    borderColor="border.accent"
    borderRadius="md"
    px="m"
    py="m"
  >
    <Text textStyle="body.xs.regular" color="text.tertiary" mb="xs">
      {label}
    </Text>
    <Text textStyle="h4.medium" color="text.primary">
      {value}
    </Text>
  </Box>
);

const PhaseTimeline = ({ phases }: { phases: SessionPlanPhase[] }): JSX.Element => {
  const total = phases.reduce((sum, phase) => sum + (phase.durationMinutes || 0), 0) || 1;

  return (
    <Box>
      <Flex h="10px" borderRadius="full" overflow="hidden" gap="1px" mb="s">
        {phases.map((phase, index) => {
          const color = PHASE_COLORS[index % PHASE_COLORS.length];
          const flex = Math.max(phase.durationMinutes || 1, 1) / total;
          return (
            <Box key={`${phase.title}-bar-${index}`} flex={flex} bg={color.solid} minW="8px" />
          );
        })}
      </Flex>
      <Flex gap="s" flexWrap="wrap">
        {phases.map((phase, index) => {
          const color = PHASE_COLORS[index % PHASE_COLORS.length];
          return (
            <HStack key={`${phase.title}-label-${index}`} gap="xs">
              <Box w="8px" h="8px" borderRadius="full" bg={color.solid} />
              <Text textStyle="body.xs.regular" color="text.tertiary">
                {phase.title} · {phase.durationMinutes}
              </Text>
            </HStack>
          );
        })}
      </Flex>
    </Box>
  );
};

const ActivitySummaryRow = ({ activity }: { activity: SessionPlanActivity }): JSX.Element => (
  <HStack justify="space-between" align="flex-start" gap="m" w="100%">
    <Text textStyle="body.s.regular" color="text.secondary" flex="1">
      {activity.title}
    </Text>
    <Text textStyle="body.xs.regular" color="text.tertiary" whiteSpace="nowrap">
      {activity.methodology}
      {activity.durationMinutes > 0 ? ` · ${activity.durationMinutes}` : ''}
    </Text>
  </HStack>
);

const ActivityDetail = ({ activity }: { activity: SessionPlanActivity }): JSX.Element => (
  <Box
    bg="background.canvas"
    borderRadius="md"
    p="m"
    border="1px solid"
    borderColor="border.accent"
  >
    <HStack justify="space-between" align="flex-start" gap="m" mb="xs">
      <Text textStyle="body.m.medium" color="text.primary">
        {activity.title}
      </Text>
      {activity.durationMinutes > 0 && (
        <Text textStyle="body.xs.regular" color="text.tertiary" whiteSpace="nowrap">
          {activity.durationMinutes} min
        </Text>
      )}
    </HStack>
    <Text textStyle="body.s.regular" color="text.secondary" mb="s">
      {activity.description}
    </Text>
    <Text textStyle="body.xs.regular" color="text.tertiary" mb="s">
      Methode: {activity.methodology}
    </Text>
    {activity.teacherInstructions && (
      <Box mb="xs">
        <Text textStyle="body.xs.medium" color="text.primary" mb="2px">
          Lehrkraft
        </Text>
        <Text textStyle="body.s.regular" color="text.secondary">
          {activity.teacherInstructions}
        </Text>
      </Box>
    )}
    {activity.studentInstructions && (
      <Box>
        <Text textStyle="body.xs.medium" color="text.primary" mb="2px">
          Lernende
        </Text>
        <Text textStyle="body.s.regular" color="text.secondary">
          {activity.studentInstructions}
        </Text>
      </Box>
    )}
  </Box>
);

const PhaseCard = ({
  phase,
  color,
  isOpen,
  onToggle,
}: {
  phase: SessionPlanPhase;
  color: PhaseColor;
  isOpen: boolean;
  onToggle: () => void;
}): JSX.Element => (
  <Box
    bg="background.primary"
    border="1px solid"
    borderColor={isOpen ? 'border.accent.active' : 'border.accent'}
    borderRadius="md"
    overflow="hidden"
    transition="border-color 0.15s ease"
  >
    <Box
      as="button"
      w="100%"
      textAlign="left"
      px="m"
      py="m"
      cursor="pointer"
      onClick={onToggle}
      _hover={{ bg: 'background.canvas' }}
    >
      <HStack justify="space-between" align="flex-start" gap="m" mb="s">
        <HStack gap="s" align="center">
          <Box w="10px" h="10px" borderRadius="full" bg={color.solid} flexShrink={0} />
          <Text textStyle="body.m.medium" color="text.primary">
            {phase.title}
          </Text>
        </HStack>
        <HStack gap="s" flexShrink={0}>
          <Text textStyle="body.s.regular" color="text.tertiary">
            {phase.durationMinutes} min
          </Text>
          <Box
            transition="transform 0.15s ease"
            transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
          >
            <NovaIcon icon={ChevronDownIcon} size="s" color="icon.quaternary" aria-hidden />
          </Box>
        </HStack>
      </HStack>
      <VStack align="stretch" gap="xs" pl="l">
        {phase.activities.map((activity, index) => (
          <ActivitySummaryRow key={`${activity.title}-${index}`} activity={activity} />
        ))}
      </VStack>
    </Box>

    <Collapsible.Root open={isOpen}>
      <Collapsible.Content>
        <Box px="m" pb="m" pt="0" borderTop="1px solid" borderColor="border.accent">
          <Text textStyle="body.s.regular" color="text.secondary" mt="m" mb="m">
            {phase.purpose}
          </Text>
          <VStack align="stretch" gap="s">
            {phase.activities.map((activity, index) => (
              <ActivityDetail key={`${activity.title}-detail-${index}`} activity={activity} />
            ))}
          </VStack>
        </Box>
      </Collapsible.Content>
    </Collapsible.Root>
  </Box>
);

export const SessionPlanDisplay = ({ plan }: Props): JSX.Element => {
  const [openPhaseIndex, setOpenPhaseIndex] = useState<number | null>(null);
  const phases = Array.isArray(plan.phases) ? plan.phases : [];
  const objectives = Array.isArray(plan.learningObjectives) ? plan.learningObjectives : [];
  const materials = Array.isArray(plan.materials) ? plan.materials : [];

  const subtitleParts = [
    `${plan.durationMinutes} Minuten`,
    plan.description && plan.description !== plan.summary ? plan.description : null,
  ].filter(Boolean);

  return (
    <VStack align="stretch" gap="xl">
      <Box>
        <Text textStyle="h3.medium" color="text.primary" mb="xs">
          {plan.title}
        </Text>
        <Text textStyle="body.s.regular" color="text.tertiary">
          {subtitleParts.join(' · ')}
        </Text>
        {plan.summary && (
          <Text textStyle="body.m.regular" color="text.secondary" mt="s">
            {plan.summary}
          </Text>
        )}
      </Box>

      <SimpleGrid columns={{ base: 2, md: 4 }} gap="s">
        <MetricCard label="Dauer" value={`${plan.durationMinutes} min`} />
        <MetricCard label="Phasen" value={String(phases.length)} />
        <MetricCard label="Lernziele" value={String(objectives.length)} />
        <MetricCard label="Materialien" value={String(materials.length)} />
      </SimpleGrid>

      {phases.length > 0 && (
        <Box>
          <SectionTitle>Stundenverlauf</SectionTitle>
          <PhaseTimeline phases={phases} />
          <VStack align="stretch" gap="s" mt="m">
            {phases.map((phase, index) => (
              <PhaseCard
                key={`${phase.title}-${index}`}
                phase={phase}
                color={PHASE_COLORS[index % PHASE_COLORS.length]}
                isOpen={openPhaseIndex === index}
                onToggle={() => setOpenPhaseIndex((current) => (current === index ? null : index))}
              />
            ))}
          </VStack>
        </Box>
      )}

      <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap="l">
        <Box>
          <SectionTitle>Lernziele</SectionTitle>
          <VStack align="stretch" gap="s">
            {objectives.map((objective, index) => {
              const badge = bloomBadgeStyles[objective.bloomLevel] ?? bloomBadgeStyles.remembering;
              return (
                <HStack key={`${objective.description}-${index}`} align="flex-start" gap="s">
                  <Box
                    as="span"
                    px="s"
                    py="2px"
                    borderRadius="full"
                    bg={badge.bg}
                    color={badge.color}
                    textStyle="body.xs.medium"
                    whiteSpace="nowrap"
                    flexShrink={0}
                  >
                    {bloomLevelLabel[objective.bloomLevel] ?? objective.bloomLevel}
                  </Box>
                  <Text textStyle="body.s.regular" color="text.secondary">
                    {objective.description}
                  </Text>
                </HStack>
              );
            })}
          </VStack>
        </Box>

        <Box>
          <SectionTitle>Materialien</SectionTitle>
          <VStack align="stretch" gap="s" mb="l">
            {materials.map((material, index) => (
              <HStack key={`${material.item}-${index}`} align="flex-start" gap="s">
                <NovaIcon icon={DocumentIcon} size="s" color="icon.quaternary" aria-hidden />
                <Text textStyle="body.s.regular" color="text.secondary">
                  {material.item}
                  {material.quantity ? ` (${material.quantity})` : ''}
                  {material.purpose ? ` — ${material.purpose}` : ''}
                </Text>
              </HStack>
            ))}
            {materials.length === 0 && (
              <Text textStyle="body.s.regular" color="text.tertiary">
                Keine Materialien hinterlegt
              </Text>
            )}
          </VStack>

          {plan.articleIds.length > 0 && (
            <Box>
              <Text textStyle="body.s.medium" color="text.primary" mb="s">
                Verknüpfte Artikel
              </Text>
              <Flex gap="s" flexWrap="wrap">
                {plan.articleIds.map((articleId, index) => (
                  <Link
                    key={articleId}
                    asChild
                    bg="background.primary"
                    border="1px solid"
                    borderColor="border.accent"
                    borderRadius="full"
                    px="m"
                    py="xs"
                    textStyle="body.xs.medium"
                    color="text.secondary"
                    _hover={{ borderColor: 'border.accent.active', color: 'text.accent' }}
                  >
                    <RouterLink to={`${ProtectedRoutes.ARTICLE}/${articleId}`}>
                      Artikel {index + 1}
                    </RouterLink>
                  </Link>
                ))}
              </Flex>
            </Box>
          )}
        </Box>
      </Grid>

      <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap="m">
        <Box
          bg="background.primary"
          border="1px solid"
          borderColor="border.accent"
          borderRadius="md"
          p="m"
          minH="160px"
        >
          <HStack gap="s" mb="s">
            <NovaIcon icon={FileIcon} size="m" color="icon.accent" aria-hidden />
            <Text textStyle="body.m.medium" color="text.primary">
              Arbeitsblatt
            </Text>
          </HStack>
          {plan.worksheetDraft ? (
            <>
              <Text textStyle="body.s.medium" color="text.primary" mb="xs">
                {plan.worksheetDraft.title}
              </Text>
              <Text textStyle="body.s.regular" color="text.secondary" mb="s">
                {plan.worksheetDraft.description}
              </Text>
              {plan.worksheetDraft.learningGoals.length > 0 && (
                <List.Root gap="xs" pl="m">
                  {plan.worksheetDraft.learningGoals.map((goal, index) => (
                    <List.Item key={`${goal}-${index}`}>
                      <Text textStyle="body.s.regular" color="text.secondary">
                        {goal}
                      </Text>
                    </List.Item>
                  ))}
                </List.Root>
              )}
              <Text textStyle="body.xs.regular" color="text.tertiary" mt="s">
                Entwurf — noch kein vollständiges Arbeitsblatt
              </Text>
            </>
          ) : (
            <Text textStyle="body.s.regular" color="text.tertiary">
              Noch kein Arbeitsblatt-Entwurf vorhanden
            </Text>
          )}
        </Box>

        <Box
          border="1px dashed"
          borderColor="border.accent"
          borderRadius="md"
          p="m"
          minH="160px"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap="s"
          bg="background.canvas"
        >
          <NovaIcon icon={BookIcon} size="l" color="icon.quaternary" aria-hidden />
          <Text textStyle="body.s.regular" color="text.tertiary" textAlign="center">
            Lernkarten noch nicht erstellt
          </Text>
        </Box>
      </Grid>
    </VStack>
  );
};
